import { useCallback, useEffect, useState } from "react";
import { Alert, Link } from "@mui/material";
import { useStateValue } from "../../context/StateValueContext";
import { useApi } from "../../hooks/useApi";
import { PERMISSIONS } from "../../constants/permissions";
import { hasPermission } from "../../functions/permissionUtils";
import { SHOPIFY_APP_STORE_URL } from "../../constants/shopify";
import { ShopifyAppStatus } from "../../types/paymentProcessor";
import ShopifyAppUpdateDialog from "./ShopifyAppUpdateDialog";

export default function ShopifyAppUpdateBanner() {
  const [{ user }] = useStateValue();
  const { get } = useApi();
  const [outdatedAccountNames, setOutdatedAccountNames] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canSeeAccounts = hasPermission(user, PERMISSIONS.ACCOUNTS_OWN);
  const companyId = user?.companyId;

  const refresh = useCallback(async () => {
    if (!canSeeAccounts) return;
    try {
      const status = await get<ShopifyAppStatus>(`/api/pp-accounts/shopify-app-status`);
      setOutdatedAccountNames(status.outdatedAccountNames);
    } catch (err) {
      console.error("Failed to refresh Shopify app status:", err);
    }
  }, [canSeeAccounts, get]);

  useEffect(() => {
    if (!canSeeAccounts || !companyId) return;
    setOutdatedAccountNames([]);
    refresh();
  }, [companyId, canSeeAccounts, refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  if (!canSeeAccounts || outdatedAccountNames.length === 0) return null;

  return (
    <>
      <Alert severity="error" sx={{ borderRadius: 0 }}>
        Please{" "}
        <Link
          component="button"
          onClick={() => setDialogOpen(true)}
          sx={{ verticalAlign: "baseline", fontWeight: 600 }}
        >
          update
        </Link>{" "}
        <strong>{outdatedAccountNames.join(", ")}</strong> to the newest version of the Chargemont
        Shopify app to ensure continued service.
      </Alert>
      <ShopifyAppUpdateDialog
        open={dialogOpen}
        accountNames={outdatedAccountNames}
        onClose={() => setDialogOpen(false)}
        onUpdate={() => window.open(SHOPIFY_APP_STORE_URL, "_blank", "noopener,noreferrer")}
      />
    </>
  );
}
